//Library Imports
import { Popover } from "antd";
import styled from "styled-components";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useHistory } from "react-router-dom";

//Local Imports
import Checkbox from "../../../modules/Common/Components/Checkbox";
import { ReactComponent as Ellipsis } from "../../../assets/img/icons/ArticlePopover/Elipsis.svg";
import { ReactComponent as DeleteWorkflow } from "../../../assets/img/icons/DeleteWorkflow.svg";
import WorkflowThumb from "../../../assets/img/WorkflowThumb.png";
import { deleteWorkflow, updateWorkflowStatusAPI } from "../actions";
import { capitalizeFirstLetter } from "utils";
import { routes, inactivateModalText } from "../constants";
import ConfirmDialog from "modules/Common/Components/ConfirmDialog";
import DialogBox from "modules/Common/Components/DialogBox";
import InfoPopover from "modules/Common/Components/InfoPopover";
import ConfirmDialogWithModal from "modules/Common/Components/ConfirmDialogWithModal";

interface IListingItem {
  workflowId: number;
  name: string;
  color: string;
  status: string;
  selectedWorkflowIds: number[];
  setSelectedWorkflowIds: (workflowIds: number[]) => void;
  isSearched: boolean;
  selectAllFlag: boolean;
  setSelectAllFlag: (value: boolean) => void;
  excludedWorkflowsIds: number[];
  setExcludedWorkflowsIds: (workflowIds: number[]) => void;
}

const ListingItem: React.FC<IListingItem> = (props: IListingItem) => {
  // Props
  const {
    workflowId,
    name,
    color,
    status,
    selectedWorkflowIds,
    setSelectedWorkflowIds,
    isSearched,
    selectAllFlag,
    setSelectAllFlag,
    excludedWorkflowsIds,
    setExcludedWorkflowsIds,
  } = props;

  // Local Variables
  const history = useHistory();
  const popoverText = "Cannot Activate an Inactive or Draft Workflow";
  const popoverTextDeleteDisable = "Cannot Delete an Active Workflow";

  // Local States
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);

  const [changeWorkflowStatusConfirmation, setChangeWorkflowStatusConfirmation] = useState<boolean>(false);

  // Redux states
  const userWorkflowsTotalCount: number = useSelector(
    (state: RootState) => state.automation.userWorkflows.totalCount
  );

  // Component Logic
  const confirmDeleteWorkflowHandler = () => {
    deleteWorkflow([workflowId], isSearched, false, [], "", "");
    setDeleteConfirmation(false);
  };

  const cancelConfirmationHandler = () => {
    setDeleteConfirmation(false);
  };

  const activateInActivateWorkflow = () => {
    setChangeWorkflowStatusConfirmation(true);
  };

  const content = () => (
    <div className="workflow-dropdown-inner">
      <div className="header-popover">
        <div className={`item-name div-style`}>
          <div onClick={activateInActivateWorkflow} className={`${status === "active" ? "" : "disabled"}`}>
            {status === "active" ? "Make Inactive" : "Make Active"}
          </div>
          <div hidden={status === "active"} className="delete-icon">
            <InfoPopover infoText={popoverText} width={225} />
          </div>
        </div>
      </div>
      <div className="content-popover">
        <div className={`item-name d-flex justify-content-between`}>
          <span onClick={() => setDeleteConfirmation(true)} className={`${status === "active" ? "disabled" : ""}`}>
            <DeleteWorkflow />
            <span className="delete-text">Delete Workflow</span>
          </span>
          <span hidden={status !== "active"} className="delete-icon">
            <InfoPopover infoText={popoverTextDeleteDisable} width={225} />
          </span>
        </div>
      </div>
    </div>
  );

  const workflowRedirectionClickHandler = () => {
    history.push(`${routes.DETAIL}?workflow_id=${workflowId}`);
  };

  const checkboxClickHandler = (checked: boolean) => {
    if (checked) {
      setSelectedWorkflowIds([...selectedWorkflowIds, workflowId]);
      if ([...selectedWorkflowIds, workflowId].length === userWorkflowsTotalCount) setSelectAllFlag(true);
      if (selectAllFlag) {
        const index = excludedWorkflowsIds.indexOf(workflowId);
        if (index > -1) {
          excludedWorkflowsIds.splice(index, 1);
        }
      }
    } else {
      setSelectedWorkflowIds(selectedWorkflowIds.filter((id) => id.toString() !== workflowId.toString()));
      if (selectAllFlag) setExcludedWorkflowsIds([...excludedWorkflowsIds, workflowId]);
    }
  };

  const proceedChangeWorkflowStatusConfirmationHandler = () => {
    updateWorkflowStatusAPI(workflowId, "inactive");
    setChangeWorkflowStatusConfirmation(false);
  };

  const closeChangeWorkflowStatusConfirmationHandler = () => {
    setChangeWorkflowStatusConfirmation(false);
  };

  return (
    <>
      <Wrapper>
        <td>
          <Checkbox
            disabled={status === "active" ? true : false}
            checked={selectedWorkflowIds.includes(workflowId)}
            onChange={(e: any) => checkboxClickHandler(e.target.checked)}
          />
        </td>
        <td>
          <div className="thumb" onClick={workflowRedirectionClickHandler}>
            <span style={{ background: `${color}` }}></span>
            <div className="image-resize" style={{ backgroundImage: `url(${WorkflowThumb})` }}></div>
            <img src={WorkflowThumb} alt="thumb" />
          </div>
        </td>
        <td>
          <h3 className="cursor-ponter" onClick={workflowRedirectionClickHandler}>
            {name}
          </h3>
          <div className="status-workflow" style={{ background: `${color}` }}>
            {capitalizeFirstLetter(status)}
          </div>
        </td>
        <td>
          {!deleteConfirmation ? (
          <Popover
            placement="leftTop"
            content={content}
            trigger="click"
            getPopupContainer={(e) => e as HTMLElement}
            overlayClassName="workflow-dropdown"
          >
            <div className="menu-ellips">
              <Ellipsis />
            </div>
          </Popover>
          ) : null }
        </td>
      </Wrapper>
      {deleteConfirmation ? (
        <DialogWrapper>
          <BackOverlay />
          <ConfirmDialogWithModal
            cancel={"Cancel"}
            confirm={"Yes"}
            onConfirm={confirmDeleteWorkflowHandler}
            onCancel={cancelConfirmationHandler}
            // icon={whiteRoy}
            title={"The selected workflow will be deleted"}
          />
        </DialogWrapper>
      ) : null}
      <DialogBox
        heading=""
        isBodyVisible={false}
        isWarning={true}
        bodyText={inactivateModalText}
        isModalOpen={changeWorkflowStatusConfirmation}
        closeHandler={closeChangeWorkflowStatusConfirmationHandler}
        closeBtnText="No"
        isCloseBtnVisible={true}
        proceedBtnText="Yes"
        proceedHandler={proceedChangeWorkflowStatusConfirmationHandler}
      />
    </>
  );
};

export default ListingItem;

const BackOverlay = styled.div`
  position: fixed;
  background: grey;
  opacity: 0.2;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
`;

const DialogWrapper = styled.div`
  z-index: 17;
  top: 50%;
  left: 50%;
  position: fixed;
`;

const Wrapper = styled.tr`
  .div-style {
    display: flex;
    justify-content: space-between;
  }
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .cursor-ponter {
    cursor: pointer;
  }
  .workflow-dropdown {
    .workflow-dropdown-inner {
      .content-popover {
        .delete-text {
          display: inline-block;
          margin-top: 2px;
        }
        span:nth-child(1) {
          display: inline-flex;
          align-items: center;
        }
      }
    }
  }
  .menu-ellips {
    cursor: pointer;
    padding: 0 5px;
  }
  h3 {
    color: #000000;
    font-weight: 500;
    font-size: 20px;
    font-family: "Roboto Slab", serif;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;
    margin-bottom: 6px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 14px;
    }
  }
  .status-workflow {
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    font-family: "Roboto", sans-serif;
    padding: 2px 10px;
    border-radius: 100px;
    display: inline;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 11px;
    }
  }
  .thumb {
    position: relative;
    cursor: pointer;
    span {
      width: 8px;
      border-radius: 6px 0 0 6px;
      height: 82px;
      position: absolute;
      left: 0;
      top: 0;
    }
    .image-resize {
      background-size: cover;
      background-repeat: no-repeat;
      z-index: 1;
      width: 102px;
      min-width: 102px;
      height: 82px;
      border-radius: 0 6px 6px 0;
      position: absolute;
      right: 0;
      top: 0;
    }
    img {
      border-radius: 6px;
      width: 102px;
      min-width: 102px;
      height: 82px;
      opacity: 0;
    }
  }
  .workflow-dropdown-inner {
    .item-name {
      .delete-icon {
        img {
          height: 16px;
          /* margin-bottom: 5px; */
        }
      }
    }
  }
`;
